import React from "react";
import {
  Box,
  Typography,
  Stack,
  Button,
  ListItem,
  ListItemIcon,
  LinearProgress,
  Skeleton,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import { ReactComponent as CheckMarkCircle } from "../../../icons/CheckMarkCircle.svg";
import { useCurrentBrand } from "../../../hooks/useCurrentWorkspace";
import GetItOnGooglePlay from "../../../icons/GetItOnGooglePlay";
import DownloadOnAppStore from "../../../icons/DownloadOnAppStore";
import { Link, Check } from "lucide-react";
import { IphoneIconPreviewCanvas } from "../IphoneIconPreviewCanvas";
import { IphoneSplashPreviewCanvas } from "../IphoneSplashPreviewCanvas";
import { SharablePreviewCanvas } from "./SharablePreviewCanvas";
import demoWorkoutSharing from "../../../assets/workout-sharing-demo.png";
import { useBrandedAppTransferStats } from "../../../hooks/useBrandedAppTransferStats";

const useStyles = makeStyles((theme) => ({
  header: {
    marginBottom: theme.spacing(2.5),
  },
  titleBox: {
    display: "flex",
    alignItems: "center",
    gap: 0,
    flexDirection: "column",
    marginBottom: theme.spacing(2),

    [theme.breakpoints.up("sm")]: {
      flexDirection: "row",
      gap: theme.spacing(2),
      marginBottom: 0,
    },
  },
  title: {
    fontSize: 32,
    fontWeight: "bold",
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  statusBox: {
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.common.white,
    padding: theme.spacing(0.5, 2),
    flexShrink: 0,
    marginBottom: 4,
  },
  status: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase",
  },
  text: {
    fontSize: 16,
    fontWeight: 500,
    color: theme.palette.text.primary,
  },
  links: {
    display: "flex",
    justifyContent: "center",
    marginTop: theme.spacing(1),
    alignItems: "center",
    flexDirection: "column",
  },
  uploadPreview: {
    width: "100%",
    display: "flex",
    justifyContent: "start",
    flexDirection: "column",
    gap: theme.spacing(2),
    alignItems: "center",
    marginBottom: theme.spacing(4),

    [theme.breakpoints.up("md")]: {
      flexDirection: "row",
      alignItems: "start",
    },
  },
  sharablePreview: {
    maxWidth: theme.spacing(42),
  },
  sectionTitle: {
    fontSize: 20,
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(3),
  },
  feature: {
    "&:not(:last-child)": {
      marginBottom: theme.spacing(3.5),
    },

    [theme.breakpoints.up("md")]: {
      "&:not(:last-child)": {
        marginBottom: theme.spacing(3.5),
      },
    },
  },
  check: {
    marginRight: theme.spacing(1.25),

    "& svg": {
      width: 30,
      height: 30,
      color: theme.palette.primary.main,
    },
  },

  successIcon: {
    width: 35,
    height: 35,
    color: theme.palette.success.main,
  },

  progressContainer: {
    padding: theme.spacing(2.5),
    backgroundColor: theme.palette.background.paper,
    borderRadius: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
  },
  progressBar: {
    height: 10,
    borderRadius: 5,
    marginTop: theme.spacing(1.5),
    marginBottom: theme.spacing(1),
    backgroundColor: theme.palette.grey[200],
    "& .MuiLinearProgress-bar": {
      borderRadius: 5,
    },
  },
  progressText: {
    fontSize: 14,
    color: theme.palette.text.secondary,
  },
  progressStats: {
    fontSize: 18,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
}));

const CopyLinkButton = ({ link }: { link: string }) => {
  const [copied, setCopied] = React.useState(false);

  const copyLink = (link: string) => {
    return () => {
      navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 1200); // show checkmark for 1.2s
    };
  };

  return (
    <Button
      variant="outlined"
      color="primary"
      fullWidth
      sx={{
        display: "flex",
        gap: 1,
      }}
      onClick={copyLink(link)}
    >
      {copied ? "Copied!" : "Copy link"}
      <Box
        sx={{
          display: "inline-flex",
          position: "relative",
          width: 20,
          height: 20,
        }}
      >
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            opacity: copied ? 0 : 1,
            transform: copied
              ? "scale(0.5) rotate(-20deg)"
              : "scale(1) rotate(0deg)",
            transition:
              "opacity 0.2s, transform 0.3s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          <Link size={20} />
        </Box>
        <Box
          sx={{
            position: "absolute",
            left: 0,
            top: 0,
            opacity: copied ? 1 : 0,
            transform: copied ? "scale(1.2)" : "scale(0.5)",
            transition:
              "opacity 0.2s, transform 0.3s cubic-bezier(0.4,0,0.2,1)",
          }}
        >
          <Check size={20} />
        </Box>
      </Box>
    </Button>
  );
};

export function PublishedAppContent() {
  const s = useStyles();
  const {
    brandName,
    appName,
    brandedAppLogo,
    AppleStoreUrl,
    GoogleStoreUrl,
    brandedAppSplashScreen,
    brandedAppSplashBackgroundColor,
    brandedAppBanner,
  } = useCurrentBrand();

  const { data: transferStats, isLoading: isLoadingStats } =
    useBrandedAppTransferStats();

  return (
    <Box className={s.header}>
      <Box className={s.titleBox}>
        <Typography className={s.title}>{brandName} Branded App</Typography>
        <Box className={s.statusBox}>
          <Typography className={s.status}>published</Typography>
        </Box>
      </Box>
      <Stack
        spacing={2}
        sx={{
          marginTop: 2,
        }}
      >
        <Typography sx={{ fontSize: 14, fontWeight: 400 }}>
          Your custom branded app is officially live🔥 on both Google Play
          Market and the Apple App Store! This means your clients can easily
          install and start using your app, creating a seamless and professional
          experience with your brand from the very first interaction.
        </Typography>
        <Typography sx={{ fontSize: 14, fontWeight: 400 }}>
          Direct links to your app are below. Your clients also have these links
          within their profiles for quick access. Now, your brand is always
          within reach, helping you stay front and center in your clients'
          fitness journeys!
        </Typography>
        <Box className={s.links}>
          <Box
            component={"img"}
            sx={{
              width: 100,
              height: 100,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              marginTop: 2,
              borderRadius: "20%",
              boxShadow: "0px 4px 10px rgba(0, 0, 0, 0.1)",
            }}
            src={brandedAppLogo}
          ></Box>
          <Typography
            sx={{
              fontSize: 16,
              fontWeight: 600,
              letterSpacing: 0.5,
              color: "#333",
              textTransform: "capitalize",
              marginTop: 1,
            }}
          >
            {appName}
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: 1,
              justifyContent: "center",
              my: 2,
              maxWidth: 350,
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <GetItOnGooglePlay href={GoogleStoreUrl} />
              <CopyLinkButton link={GoogleStoreUrl} />
            </Box>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <DownloadOnAppStore href={AppleStoreUrl} />
              <CopyLinkButton link={AppleStoreUrl} />
            </Box>
          </Box>
        </Box>
        <Typography
          sx={{
            fontSize: 14,
            fontWeight: 400,
          }}
        >
          No effort from you is required to migrate your existing and future
          clients to your brand new mobile app from Stridist. We're promoting it
          to your existing clients, ensuring they continue their fitness journey
          with your brand only. Every future client of yours will not know what
          is Stridist - only <b>{appName}</b> from now on.
        </Typography>

        <Typography className={s.sectionTitle} sx={{ pt: 5 }}>
          Client Transfer Progress
        </Typography>
        <Box className={s.progressContainer}>
          {isLoadingStats ? (
            <>
              <Skeleton variant="text" width={200} height={32} />
              <Skeleton
                variant="rectangular"
                height={10}
                sx={{ borderRadius: 5, my: 1.5 }}
              />
              <Skeleton variant="text" width={300} />
            </>
          ) : (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                {(transferStats?.transferPercentage ?? 0) === 100 && (
                  <CheckMarkCircle className={s.successIcon} />
                )}
                <Typography className={s.progressStats}>
                  {(transferStats?.transferPercentage ?? 0) === 100
                    ? "All clients transferred!"
                    : `${transferStats?.transferredClients ?? 0} of ${transferStats?.totalClients ?? 0} clients transferred`}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={transferStats?.transferPercentage ?? 0}
                className={s.progressBar}
                color={
                  (transferStats?.transferPercentage ?? 0) === 100
                    ? "success"
                    : "primary"
                }
              />
              <Typography className={s.progressText}>
                {(transferStats?.transferPercentage ?? 0) === 100
                  ? `All ${transferStats?.totalClients} clients have successfully moved to your branded app.`
                  : "Clients are counted as transferred once they log in using your branded app. Share the download links above with your clients to help them make the switch."}
              </Typography>
            </>
          )}
        </Box>

        <Typography className={s.sectionTitle} sx={{ pt: 5 }}>
          Your Branding
        </Typography>
        <ListItem className={s.feature}>
          <ListItemIcon className={s.check} children={<CheckMarkCircle />} />
          <Typography className={s.text}>App icon and splash screen</Typography>
        </ListItem>
        <Box className={s.uploadPreview}>
          <Box className={s.sharablePreview}>
            <IphoneIconPreviewCanvas
              iconUrl={brandedAppLogo}
              appName={appName}
              previewTitle="Branded App Preview"
            />
          </Box>
          <IphoneSplashPreviewCanvas
            splashUrl={brandedAppSplashScreen}
            backgroundColor={brandedAppSplashBackgroundColor}
          />
        </Box>

        <ListItem className={s.feature}>
          <ListItemIcon className={s.check} children={<CheckMarkCircle />} />
          <Typography className={s.text}>
            Internal app branding elements
          </Typography>
        </ListItem>
        <Box className={s.uploadPreview}>
          <SharablePreviewCanvas
            brandedAppBanner={brandedAppBanner}
            backgroundUrl={demoWorkoutSharing}
            title="Workout Sharing Preview"
          />
          <SharablePreviewCanvas brandedAppBanner={brandedAppBanner} />
        </Box>
      </Stack>
    </Box>
  );
}
